import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

/**
 * Refuerzo de área segura en WebView nativo (sobre todo Android): a veces
 * `env(safe-area-inset-*)` resuelve a 0 aunque el contenido vaya bajo status bar o barra de navegación.
 * Publica `--sat-floating-panel-extra-top` y `--sat-floating-panel-extra-bottom` en `documentElement`
 * para sumarlas en SCSS junto con `env()`. En navegador / iOS con env correcto, los extras quedan en 0.
 */
@Injectable({ providedIn: 'root' })
export class NativeFloatingPanelSafeAreaService {
  /** Registrar listeners y aplicar una primera vez (solo plataforma nativa). */
  init(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    this.apply();
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }
    const onResize = (): void => {
      this.apply();
    };
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
  }

  private readEnvInset(edge: 'top' | 'bottom'): number {
    const el = document.createElement('div');
    const cssPad = edge === 'top' ? 'padding-top' : 'padding-bottom';
    const jsPad = edge === 'top' ? 'paddingTop' : 'paddingBottom';
    el.style.cssText = [
      'position:fixed',
      'left:-9999px',
      'top:0',
      'width:1px',
      'height:1px',
      'visibility:hidden',
      'pointer-events:none',
      `${cssPad}:env(safe-area-inset-${edge}, 0px)`,
    ].join(';');
    document.body.appendChild(el);
    const raw = getComputedStyle(el)[jsPad];
    document.body.removeChild(el);
    return parseFloat(raw) || 0;
  }

  private apply(): void {
    const root = document.documentElement;
    const envTop = this.readEnvInset('top');
    const envBottom = this.readEnvInset('bottom');
    let extraTop = 0;
    let extraBottom = 0;
    if (Capacitor.getPlatform() === 'android') {
      // Heurística conservadora cuando el WebView no informa recorte (muy habitual en Android).
      if (envTop < 1) {
        extraTop = 28;
      }
      if (envBottom < 1) {
        extraBottom = 48;
      }
    }
    root.style.setProperty('--sat-floating-panel-extra-top', `${extraTop}px`);
    root.style.setProperty('--sat-floating-panel-extra-bottom', `${extraBottom}px`);
  }
}
